import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { uuidSchema } from "./platform-common.js";
export const STRIPE_BILLING_CONTRACT_VERSION = 1;
export const MAX_STRIPE_EVENT_PROJECTION_BYTES = 4_096;
export const MAX_STRIPE_RECONCILIATION_ATTEMPTS = 20;
export const MAX_STRIPE_SUBSCRIPTIONS_PER_CUSTOMER = 100;
const stripeIdentifier = (prefix) => z
    .string()
    .min(prefix.length + 1)
    .max(255)
    .startsWith(prefix)
    .regex(new RegExp(`^${prefix}[A-Za-z0-9]+$`));
export const stripeCustomerIdSchema = stripeIdentifier("cus_");
export const stripeSubscriptionIdSchema = stripeIdentifier("sub_");
export const stripeEventIdSchema = stripeIdentifier("evt_");
export const stripeProductIdSchema = stripeIdentifier("prod_");
export const stripeCheckoutSessionIdSchema = z
    .string()
    .min(9)
    .max(255)
    .regex(/^cs_(?:test|live)_[A-Za-z0-9]+$/);
export const stripePlanKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/);
export const stripeDiagnosticCodeSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/);
export const stripeApiVersionSchema = z
    .string()
    .min(10)
    .max(64)
    .regex(/^\d{4}-\d{2}-\d{2}(?:\.[a-z][a-z0-9_-]*)?$/);
export const stripeEventTypeSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9_.]+$/);
export const stripeObjectIdSchema = z
    .string()
    .min(3)
    .max(255)
    .regex(/^[a-z][a-z0-9]*_[A-Za-z0-9]+$/);
export const stripeSubscriptionStatusSchema = z.enum([
    "trialing",
    "active",
    "past_due",
    "unpaid",
    "paused",
    "canceled",
    "incomplete",
    "incomplete_expired",
]);
export const stripeEventProjectionSchema = z
    .object({
    reconciliationReason: z.enum([
        "subscription",
        "checkout",
        "customer",
        "refund",
        "dispute",
    ]),
    customerId: stripeCustomerIdSchema.nullable(),
    subscriptionId: stripeSubscriptionIdSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.customerId === null && value.subscriptionId === null) {
        context.addIssue({
            code: "custom",
            message: "A Stripe event must identify a customer or subscription",
            path: ["customerId"],
        });
    }
    if (new TextEncoder().encode(JSON.stringify(value)).byteLength >
        MAX_STRIPE_EVENT_PROJECTION_BYTES) {
        context.addIssue({
            code: "custom",
            message: "Stripe event projection is too large",
        });
    }
});
export const verifiedStripeEventSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    eventType: stripeEventTypeSchema,
    objectId: stripeObjectIdSchema,
    apiVersion: stripeApiVersionSchema,
    livemode: z.boolean(),
    eventCreatedAt: utcTimestampSchema,
    projection: stripeEventProjectionSchema,
})
    .strict();
export const stripeWebhookIngestionResultSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    inserted: z.boolean(),
})
    .strict();
export const stripeCustomerClaimInputSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    livemode: z.literal(false),
    claimId: uuidSchema,
    operationId: uuidSchema,
})
    .strict();
export const stripeCustomerClaimSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    state: z.enum(["claimed", "existing", "busy"]),
    claimId: uuidSchema.nullable(),
    operationId: uuidSchema,
    operationCreatedAt: utcTimestampSchema,
    customerId: stripeCustomerIdSchema.nullable(),
    version: z.int().positive(),
})
    .strict();
export const stripeCustomerClaimCompletionSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    livemode: z.literal(false),
    claimId: uuidSchema,
    operationId: uuidSchema,
    customerId: stripeCustomerIdSchema,
})
    .strict();
export const stripeReconciliationClaimSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    claimId: uuidSchema,
    guildId: discordSnowflakeSchema.nullable(),
    eventType: stripeEventTypeSchema,
    livemode: z.boolean(),
    attemptCount: z.int().min(1).max(MAX_STRIPE_RECONCILIATION_ATTEMPTS),
    projection: stripeEventProjectionSchema,
    leaseExpiresAt: utcTimestampSchema,
})
    .strict();
export const stripeSubscriptionSnapshotSchema = z
    .object({
    subscriptionId: stripeSubscriptionIdSchema,
    status: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z_]+$/),
    productId: stripeProductIdSchema.nullable(),
    cancelAtPeriodEnd: z.boolean(),
    currentPeriodEnd: utcTimestampSchema.nullable(),
    providerCreatedAt: utcTimestampSchema,
    providerUpdatedAt: utcTimestampSchema,
})
    .strict()
    .refine((value) => value.providerUpdatedAt >= value.providerCreatedAt, "Provider update cannot precede creation");
export const stripeSubscriptionListSchema = z
    .array(stripeSubscriptionSnapshotSchema)
    .max(MAX_STRIPE_SUBSCRIPTIONS_PER_CUSTOMER)
    .superRefine((subscriptions, context) => {
    const seen = new Set();
    for (const [index, subscription] of subscriptions.entries()) {
        if (seen.has(subscription.subscriptionId)) {
            context.addIssue({
                code: "custom",
                message: "Stripe subscription IDs must be unique",
                path: [index, "subscriptionId"],
            });
        }
        seen.add(subscription.subscriptionId);
    }
});
export const currentStripeSubscriptionStateSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    claimId: uuidSchema,
    guildId: discordSnowflakeSchema,
    customerId: stripeCustomerIdSchema,
    livemode: z.boolean(),
    subscriptions: stripeSubscriptionListSchema,
})
    .strict();
export const stripeStateSweepClaimSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    claimId: uuidSchema,
    customerId: stripeCustomerIdSchema,
    livemode: z.boolean(),
    attemptCount: z.int().min(1).max(MAX_STRIPE_RECONCILIATION_ATTEMPTS),
    knownSubscriptionIds: z
        .array(stripeSubscriptionIdSchema)
        .max(MAX_STRIPE_SUBSCRIPTIONS_PER_CUSTOMER),
    leaseExpiresAt: utcTimestampSchema,
})
    .strict();
export const stripeStateSweepCompletionSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    claimId: uuidSchema,
    customerId: stripeCustomerIdSchema,
    livemode: z.boolean(),
    subscriptions: stripeSubscriptionListSchema,
})
    .strict();
export const stripeCheckoutClaimInputSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    livemode: z.literal(false),
    planKey: stripePlanKeySchema,
    claimId: uuidSchema,
    operationId: uuidSchema,
})
    .strict();
export const stripeCheckoutClaimSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    state: z.enum(["create", "retrieve", "reuse", "busy", "blocked"]),
    claimId: uuidSchema.nullable(),
    operationId: uuidSchema.nullable(),
    operationCreatedAt: utcTimestampSchema.nullable(),
    sessionId: stripeCheckoutSessionIdSchema.nullable(),
    sessionStatus: z.enum(["open", "complete", "expired"]).nullable(),
    sessionExpiresAt: utcTimestampSchema.nullable(),
    leaseExpiresAt: utcTimestampSchema.nullable(),
})
    .strict();
export const stripeCheckoutSessionCompletionSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    livemode: z.literal(false),
    claimId: uuidSchema,
    operationId: uuidSchema,
    sessionId: stripeCheckoutSessionIdSchema,
    sessionStatus: z.enum(["open", "complete", "expired"]),
    sessionExpiresAt: utcTimestampSchema,
})
    .strict();
export const stripeReconciliationRetrySchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    claimId: uuidSchema,
    errorCode: stripeDiagnosticCodeSchema,
    retryAfterSeconds: z.int().min(1).max(86_400),
})
    .strict();
export const stripeStateSweepRetrySchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    livemode: z.boolean(),
    claimId: uuidSchema,
    errorCode: stripeDiagnosticCodeSchema,
    retryAfterSeconds: z.int().min(1).max(86_400),
})
    .strict();
export const stripeWebhookRequeueSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    requeueId: uuidSchema,
    reasonCode: stripeDiagnosticCodeSchema,
})
    .strict();
export const stripeWebhookRequeueResultSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    previousErrorCode: stripeDiagnosticCodeSchema,
    previousAttemptCount: z
        .int()
        .min(1)
        .max(MAX_STRIPE_RECONCILIATION_ATTEMPTS),
    previousDeadLetteredAt: utcTimestampSchema,
    requeuedAt: utcTimestampSchema,
})
    .strict();
export const stripeStateSweepRequeueSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    livemode: z.literal(false),
    requeueId: uuidSchema,
    reasonCode: stripeDiagnosticCodeSchema,
})
    .strict();
export const stripeStateSweepRequeueResultSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    previousErrorCode: stripeDiagnosticCodeSchema,
    previousAttemptCount: z
        .int()
        .min(0)
        .max(MAX_STRIPE_RECONCILIATION_ATTEMPTS),
    previousExhaustionCount: z.int().positive(),
    previousExhaustedAt: utcTimestampSchema,
    requeuedAt: utcTimestampSchema,
})
    .strict();
export const stripeRetentionPurgeResultSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    webhookEventCount: z.int().nonnegative().max(500),
    checkoutAttemptCount: z.int().nonnegative().max(500),
    totalCount: z.int().nonnegative().max(500),
})
    .strict();
export const stripeReconciliationResultSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    eventId: stripeEventIdSchema,
    guildId: discordSnowflakeSchema,
    providerAccessActive: z.boolean(),
    policyDecision: z.enum([
        "active_approved_product",
        "live_mode_blocked",
        "missing_subscription",
        "non_active_status",
        "unknown_product",
        "period_ended",
    ]),
    entitlementRevision: z.int().positive(),
})
    .strict();
export const stripeBillingStatusSchema = z
    .object({
    contractVersion: z.literal(STRIPE_BILLING_CONTRACT_VERSION),
    guildId: discordSnowflakeSchema,
    customerReady: z.boolean(),
    status: z.string().min(1).max(64).nullable(),
    cancelAtPeriodEnd: z.boolean(),
    currentPeriodEnd: utcTimestampSchema.nullable(),
    providerAccessActive: z.boolean(),
    reconciliationPending: z.boolean(),
    reconciliationPendingCount: z.int().nonnegative(),
    reconciliationDeadLetterCount: z.int().nonnegative(),
    stateSweepExhaustedCount: z.int().min(0).max(1).optional(),
    operatorAttentionCount: z.int().nonnegative().optional(),
    stateSweepExhaustionCount: z.int().nonnegative().optional(),
    stateSweepExhaustedAt: utcTimestampSchema.nullable().optional(),
    stateSweepLastErrorCode: stripeDiagnosticCodeSchema.nullable().optional(),
    needsOperatorAttention: z.boolean(),
    lastReconciledAt: utcTimestampSchema.nullable(),
})
    .strict();
//# sourceMappingURL=stripe-billing.js.map